// Riwayat pembuatan dokumen, on the Template page for a Non-Penyedia: the
// newest 15 documents the user made from a template or a PBJ output (GET
// /templates/history), both Pluto drafts and Unduh .docx. A draft opens its
// conversation (inside its project); a download is fetched again with the
// same project. Reloads after every Unduh .docx (DOCX_SAVED_EVENT).
import { useEffect, useRef } from "react";
import * as API from "../api";
import { formatDateTime } from "../lib/format";
import { Link } from "../router";
import { conversationPath } from "../screens/Chat";
import { useFeatures } from "../shell/features";
import { EmptyState, Notice, SectionHead, SkeletonRows, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";
import { canDocx, DOCX_SAVED_EVENT, useDocx } from "./docx";

function again(d: API.TemplateHistoryItem): Promise<API.DocxFile> {
  const projectId = d.projectId ?? undefined;
  return d.templateId ? API.downloadTemplateDocx(d.templateId, projectId) : API.downloadNodeDocx(d.nodeId ?? "", projectId);
}

export function RecentDocs() {
  const features = useFeatures();
  const history = useAsync(() => API.getTemplateHistory(), []);
  const docx = useDocx();
  const docxOn = canDocx(features);
  const { reload } = history;
  // reload is a new function each render: listen once, call the latest.
  const latest = useRef(reload);
  latest.current = reload;

  useEffect(() => {
    const onSaved = () => latest.current();
    window.addEventListener(DOCX_SAVED_EVENT, onSaved);
    return () => window.removeEventListener(DOCX_SAVED_EVENT, onSaved);
  }, []);

  const list = history.data ?? [];
  return (
    <section className="region" aria-labelledby="tp-history-title">
      <SectionHead id="tp-history-title" title="Riwayat pembuatan dokumen" lead="Draf dari Pluto dan unduhan .docx terakhir Anda, paling baru di atas." />
      {history.loading && !history.data ? (
        <SkeletonRows count={3} label="Memuat riwayat" />
      ) : history.error ? (
        <Notice tone="danger" title="Riwayat belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={reload}>Muat ulang</button>}>
          <p>{API.errorMessage(history.error)}</p>
        </Notice>
      ) : list.length === 0 ? (
        <EmptyState icon="documentText" title="Belum ada dokumen" body="Dokumen yang Anda susun dengan Pluto atau unduh sebagai .docx akan tampil di sini." />
      ) : (
        <ul className="rows" aria-label="Riwayat pembuatan dokumen">
          {list.map((d) => (
            <li key={d.id} className="row">
              <span className="row-main">
                <span className="row-title">{d.title}</span>
                <span className="row-meta">
                  {d.kind === "draf" ? "Draf Pluto" : "Unduhan .docx"}. {d.projectName ?? "Tanpa project"}. {formatDateTime(d.createdAt)}
                </span>
                {docx.error?.key === d.id ? (
                  <span className="pk-item-error" role="alert">
                    <Glyph name="danger" size={16} />
                    <span>{docx.error.message}</span>
                  </span>
                ) : null}
              </span>
              <span className="row-side">
                {d.kind === "draf" && d.conversationId ? (
                  <Link className="btn btn-secondary" href={conversationPath(d.conversationId, d.projectId ?? undefined)} aria-label={`Buka draf ${d.title}`}>
                    <Glyph name="messages" />
                    <span>Buka draf</span>
                  </Link>
                ) : d.kind === "docx" && docxOn ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={docx.busy !== null}
                    aria-busy={docx.busy === d.id || undefined}
                    aria-label={`Unduh lagi ${d.title}`}
                    onClick={() => void docx.start(d.id, () => again(d))}
                  >
                    <Glyph name="documentDownload" />
                    <span>{docx.busy === d.id ? "Menyiapkan" : "Unduh lagi"}</span>
                  </button>
                ) : d.kind === "draf" ? (
                  <span className="row-meta">Percakapan sudah dihapus</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
